import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Loader2, KeyRound, Smartphone, Download, Flag, FileQuestion, ScrollText, ArrowLeft } from "lucide-react";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";
const REQUEST_TYPES = [["access", "Access my data"], ["correction", "Correct my data"], ["deletion", "Delete my data"],
  ["portability", "Export / portability"], ["opt_out", "Opt out of processing"], ["consent_history", "Consent history"],
  ["identify_disclosures", "Who received my data"], ["human_review", "Request human review"], ["privacy_concern", "Report a privacy concern"]];

export default function MySecurity() {
  const [data, setData] = useState(null);
  const [consents, setConsents] = useState(null);
  const [requests, setRequests] = useState([]);
  const [reqType, setReqType] = useState("access");
  const [pw, setPw] = useState("");
  const [report, setReport] = useState("");
  const [policies, setPolicies] = useState([]);
  const [training, setTraining] = useState([]);

  const load = useCallback(() => {
    api.get("/my-security").then(({ data }) => setData(data)).catch(() => {});
    api.get("/privacy/consents").then(({ data }) => setConsents(data)).catch(() => {});
    api.get("/privacy/requests").then(({ data }) => setRequests(data)).catch(() => {});
    api.get("/security/policies").then(({ data }) => setPolicies(data)).catch(() => {});
    api.get("/security/training").then(({ data }) => setTraining(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;

  const submitRequest = async () => {
    try {
      const { data: r } = await api.post("/privacy/requests", { request_type: reqType });
      toast.success(`Request submitted — response due by ${new Date(r.deadline).toLocaleDateString()} (${r.deadline_authority.slice(0, 60)}…)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const verifyIdentity = async (rid) => {
    const password = window.prompt("Verify your identity — enter your password:");
    if (!password) return;
    try { await api.post(`/privacy/requests/${rid}/verify-identity`, { password }); toast.success("Identity verified"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const downloadData = async () => {
    if (!pw) { toast.error("Enter your password to reauthenticate"); return; }
    try {
      const { data: link } = await api.post("/privacy/export", { password: pw });
      const res = await api.get(link.download_path.replace("/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "crash_ready_my_data.json"; a.click();
      URL.revokeObjectURL(url); setPw("");
      toast.success("Your data export downloaded (one-time expiring link, logged)");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
  };
  const toggleConsent = async (type, on) => {
    try {
      await api.post(`/privacy/consents/${type}/${on ? "grant" : "withdraw"}`, {});
      toast.success(on ? "Consent recorded" : "Consent withdrawn — future optional processing stops; prior evidence preserved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-security">
      <header className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Link to="/dashboard" className="text-white/50 text-xs flex items-center gap-1 hover:text-white"><ArrowLeft className="w-3.5 h-3.5" /> Dashboard</Link>
          <h1 className="font-heading text-2xl font-extrabold mt-2 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-cobalt" /> My Security</h1>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-6 space-y-5">
        {/* status */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={KeyRound} label="Password" value={data.password_set ? "Set" : "Not set"} tone={data.password_set} testid="sec-password" />
          <Stat icon={Smartphone} label="MFA" value={data.mfa_enabled ? "Enabled" : data.mfa_required ? `Required — enroll by ${data.mfa_grace_deadline?.slice(0, 10) || "soon"}` : "Available"} tone={data.mfa_enabled} testid="sec-mfa" />
          <Stat icon={ScrollText} label="Active sessions" value={data.active_sessions} tone testid="sec-sessions" />
          <Stat icon={FileQuestion} label="Privacy requests" value={data.privacy_requests} tone testid="sec-requests" />
        </div>
        {data.mfa_required && !data.mfa_enabled && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-xs text-amber-700" data-testid="sec-mfa-banner">
            Your role requires multi-factor authentication. Enroll from your <Link to="/profile" className="font-bold underline">Profile → Security</Link> before the grace period ends{data.mfa_grace_deadline ? ` (${data.mfa_grace_deadline.slice(0, 10)})` : ""} or sign-in will be blocked.
          </div>
        )}

        {/* sessions */}
        <section className="rounded-3xl bg-white border border-slate-100 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-navy text-sm">Signed-in devices &amp; sessions</h2>
            <button onClick={async () => { const { data: r } = await api.post("/my-security/sessions/revoke", {}); toast.success(`${r.revoked} other session(s) revoked`); load(); }}
              data-testid="sec-revoke-all" className="px-4 py-1.5 rounded-full bg-red-50 text-red-500 text-xs font-bold">Sign out other devices</button>
          </div>
          <div className="mt-3 space-y-1.5 text-xs text-slate-500">
            {(data.sessions || []).slice(0, 6).map((s, i) => (
              <div key={i} className="flex justify-between rounded-xl bg-slate-50 px-3 py-2">
                <span>Session started {new Date(s.created_at).toLocaleString()}</span>
                <span className="text-slate-300">expires {new Date(s.expires_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </section>

        {/* policy acknowledgments + training */}
        {(policies.length > 0 || training.length > 0) && (
          <div className="grid md:grid-cols-2 gap-4">
            <section className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="sec-policies">
              <h2 className="font-heading font-bold text-navy text-sm">Policy acknowledgments</h2>
              <div className="mt-3 space-y-1.5">
                {policies.map((p) => (
                  <div key={p.policy_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 text-xs" data-testid={`sec-policy-${p.policy_id}`}>
                    <span className="text-slate-600">{p.name} <span className="text-slate-300">v{p.version}</span></span>
                    {p.acknowledged ? <span className="text-emerald-600 font-bold text-[10px]">✓ Acknowledged</span> : (
                      <button onClick={async () => { try { await api.post(`/security/policies/${p.policy_id}/acknowledge`); toast.success("Acknowledgment recorded (audited)"); load(); } catch { toast.error("Failed"); } }}
                        data-testid={`sec-ack-${p.policy_id}`} className="px-3 py-1 rounded-full bg-navy text-white text-[10px] font-bold">Acknowledge</button>
                    )}
                  </div>
                ))}
                {!policies.length && <p className="text-[11px] text-slate-300">No published policies apply to your role yet.</p>}
              </div>
            </section>
            <section className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="sec-training">
              <h2 className="font-heading font-bold text-navy text-sm">My security training</h2>
              <div className="mt-3 space-y-1.5">
                {training.map((t) => (
                  <div key={t.assignment_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 text-xs" data-testid={`sec-trn-${t.assignment_id}`}>
                    <span className="text-slate-600">{t.course_name}{t.status === "assigned" ? <span className="text-slate-300"> · due {t.due?.slice(0, 10)}</span> : null}</span>
                    {t.status === "completed" ? <span className="text-emerald-600 font-bold text-[10px]">✓ Completed{t.refresher_due ? ` · refresher ${t.refresher_due.slice(0, 10)}` : ""}</span> : (
                      <button onClick={async () => { try { await api.post(`/security/training/${t.assignment_id}/complete`); toast.success("Training completed — evidence recorded"); load(); } catch { toast.error("Failed"); } }}
                        data-testid={`sec-trn-complete-${t.assignment_id}`} className="px-3 py-1 rounded-full bg-cobalt text-white text-[10px] font-bold">Mark complete</button>
                    )}
                  </div>
                ))}
                {!training.length && <p className="text-[11px] text-slate-300">No training assigned to you yet.</p>}
              </div>
            </section>
          </div>
        )}

        {/* consents */}
        <section className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="sec-consents">
          <h2 className="font-heading font-bold text-navy text-sm">Optional consent preferences</h2>
          <p className="text-[11px] text-slate-400 mt-0.5">Withdrawing consent stops future optional processing. Records we must legally retain are preserved, and your prior consent history stays intact as evidence.</p>
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            {(consents?.optional_types || []).map((t) => {
              const on = consents?.current?.[t];
              return (
                <div key={t} className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2.5">
                  <span className="text-xs text-slate-600 capitalize">{t.replace(/_/g, " ")}</span>
                  <button onClick={() => toggleConsent(t, !on)} data-testid={`sec-consent-${t}`}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold ${on ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                    {on ? "Granted — withdraw" : "Not granted — grant"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* privacy requests */}
        <section className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="sec-privacy-center">
          <h2 className="font-heading font-bold text-navy text-sm">Privacy Request Center</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <select value={reqType} onChange={(e) => setReqType(e.target.value)} data-testid="sec-request-type" className={`${inputCls} max-w-xs`}>
              {REQUEST_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button onClick={submitRequest} data-testid="sec-request-submit" className="px-5 py-2 rounded-full bg-navy text-white text-xs font-bold">Submit request</button>
          </div>
          <div className="mt-4 space-y-2">
            {requests.map((r) => (
              <div key={r.request_id} className="rounded-xl border border-slate-100 px-4 py-3 text-xs" data-testid={`sec-request-${r.request_id}`}>
                <div className="flex justify-between items-center">
                  <b className="text-navy capitalize">{r.request_type.replace(/_/g, " ")}</b>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">{r.status}</span>
                </div>
                <div className="text-slate-400 mt-1">Due {new Date(r.deadline).toLocaleDateString()} · {r.deadline_authority.slice(0, 70)}</div>
                {!r.identity_verified && <button onClick={() => verifyIdentity(r.request_id)} data-testid={`sec-verify-${r.request_id}`} className="mt-2 px-3 py-1 rounded-full bg-cobalt text-white text-[10px] font-bold">Verify my identity</button>}
                {(r.status === "Denied" || r.status === "Partially Approved") && (
                  <button onClick={async () => { await api.post(`/privacy/requests/${r.request_id}/appeal`, { reason: "I disagree with this outcome" }); toast.success("Appeal filed"); load(); }}
                    data-testid={`sec-appeal-${r.request_id}`} className="mt-2 ml-2 px-3 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">Appeal</button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* download + report */}
        <div className="grid md:grid-cols-2 gap-4">
          <section className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="sec-download">
            <h2 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Download className="w-4 h-4 text-cobalt" /> Download my data</h2>
            <p className="text-[11px] text-slate-400 mt-1">Requires reauthentication. The link expires in 15 minutes, works once, excludes anyone else's data, and every download is logged.</p>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Your password" data-testid="sec-export-password" className={`${inputCls} mt-2`} />
            <button onClick={downloadData} data-testid="sec-export-btn" className="mt-2 w-full py-2.5 rounded-full bg-navy text-white text-xs font-bold">Generate secure export</button>
          </section>
          <section className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="sec-report">
            <h2 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Flag className="w-4 h-4 text-red-500" /> Report suspicious activity</h2>
            <textarea value={report} onChange={(e) => setReport(e.target.value)} rows={2} placeholder="Describe what you noticed…" data-testid="sec-report-text" className={`${inputCls} mt-2`} />
            <button onClick={async () => { try { await api.post("/my-security/report", { description: report }); toast.success("Reported — thank you"); setReport(""); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } }}
              data-testid="sec-report-btn" className="mt-2 w-full py-2.5 rounded-full bg-red-500 text-white text-xs font-bold">Send report</button>
          </section>
        </div>

        <section className="rounded-3xl bg-navy text-white p-5 text-xs">
          <b className="font-heading">Security tips</b>
          <ul className="mt-2 grid sm:grid-cols-2 gap-1.5 text-white/70">
            {data.education.map((e, i) => <li key={i}>• {e}</li>)}
          </ul>
        </section>
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid={testid}>
    <Icon className={`w-4 h-4 ${tone ? "text-emerald-500" : "text-amber-500"}`} />
    <div className="text-[10px] font-bold uppercase text-slate-400 mt-1.5">{label}</div>
    <div className="font-heading font-bold text-navy text-sm mt-0.5">{String(value)}</div>
  </div>
);
